import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { keys } from '../lib/mutate.ts';
import { addDays, hours, longLabel, range, today, type Ymd } from '../lib/dates.ts';
import { Banner, NotYet, Spinner } from '../ui/bits.tsx';

/**
 * "Mijn planning" — the worker's screen. Mobile-first: this is read one-handed, outdoors.
 *
 * It calls `/planning/my-schedule`, which takes NO actor parameter: the backend resolves the viewer's
 * own resource rows from the session. There is nothing here for a curious worker to tamper with.
 */
export function MySchedule() {
  const [from, setFrom] = useState<Ymd>(today());
  const to = addDays(from, 13);
  const q = useQuery({ queryKey: keys.mySchedule(from, to), queryFn: () => A.getMySchedule(from, to) });
  const byDay = useMemo(() => {
    const m = new Map<Ymd, A.ScheduleItem[]>();
    for (const i of q.data?.items ?? []) {
      if (!m.has(i.day)) m.set(i.day, []);
      m.get(i.day)!.push(i);
    }
    return m;
  }, [q.data]);

  return (
    <div className="stack">
      <div className="row">
        <button className="btn small" onClick={() => setFrom(addDays(from, -14))}>‹ Vorige</button>
        <button className="btn small" onClick={() => setFrom(today())}>Vandaag</button>
        <button className="btn small" onClick={() => setFrom(addDays(from, 14))}>Volgende ›</button>
      </div>
      {q.isPending && <Spinner />}
      {q.isError && <Banner kind="err">{(q.error as Error).message}</Banner>}
      {q.data && q.data.items.length === 0 && (
        <Banner kind="info">Je hebt geen ingeplande taken in deze periode.</Banner>
      )}
      {range(from, to).map((d) => {
        const list = byDay.get(d) ?? [];
        if (list.length === 0) return null;
        return (
          <div key={d} className="card stack">
            <strong>{longLabel(d)}</strong>
            {list.map((i) => (
              <div key={i.assignmentId} className="stack" style={{ gap: 4 }}>
                {/* The werf first — a technician driving to a site is looking for the place, not for
                    the task name and certainly not for the project code. */}
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="werf-name">{A.werfLabel(i).name}</span>
                  <span className="faint">{hours(i.minutes)}</span>
                </div>
                <strong style={{ fontSize: 14 }}>{i.title}</strong>
                <span className="faint">
                  {A.werfLabel(i).code ? `${A.werfLabel(i).code} · ` : ''}
                  {i.customerName ?? '—'}
                  {i.disciplineCode ? ` · ${A.disciplineLabel(i.disciplineCode)}` : ''}
                </span>
                {i.scope && <p className="job-note" style={{ margin: 0 }}>{i.scope}</p>}
                <div className="row">
                  {/* THE BACKEND HAS NO DOMAIN FOR THESE YET. Shown as visibly unavailable rather than
                      hidden, so the crew can see what is coming and nobody thinks it silently failed. */}
                  <NotYet what="uren registreren"><button className="btn small" disabled>Uren</button></NotYet>
                  <NotYet what="foto toevoegen"><button className="btn small" disabled>Foto</button></NotYet>
                  <NotYet what="taak afronden"><button className="btn small" disabled>Afronden</button></NotYet>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
